import {
  AgCartesianAxisOptions,
  AgCartesianSeriesOptions,
  AgChartOptions,
  AgSeriesVisibilityChange,
} from 'ag-charts-enterprise';
import { AgCharts } from 'ag-charts-react';
import { TFunction, useTranslation } from 'translations/hooks';
import { getAgChartLocale } from 'translations/locale';

import { useSingleGraphReferenceContext } from 'business/providers/graph-reference';
import { useReportProdAnalyzeFilters } from 'business/shift-report/report-analyze/pages/production-analyze/providers/filters';
import { useReportProdAnalyzeSeriesFilter } from 'business/shift-report/report-analyze/pages/production-analyze/providers/series-filter';
import {
  ReportProdAnalyzeGraphPoint,
  SerieMetadata,
} from 'business/shift-report/report-analyze/pages/production-analyze/types';
import { useChartResize } from 'technical/chart-resize';
import { hasInitialZoom } from 'technical/graph/has-initial-zoom';
import { useMediaType } from 'technical/media/hooks';
import { formatDateToDayAndMonth } from 'technical/time-utils/format-date-to-day-and-month';

type ReportProdAnalyzeGraphProps = {
  reportProdAnalyzeGraphData?: ReportProdAnalyzeGraphPoint[];
  reportProdAnalyzeUsedSeries: string[];
  availableSeries: SerieMetadata[];
};

const tooltipRenderer = (params: any, t: TFunction) => {
  return {
    data: [
      {
        label: '',
        value: t('reportAnalyze.productionAnalyze.graph.tooltip.ring', {
          count: parseInt(params.datum[params.yKey]),
        }),
      },
    ],
  };
};

const buildReportProdAnalyzeSeries = (
  availableSeries: SerieMetadata[],
  usedSeries: string[],
  selectedSeries: Record<string, boolean>,
  t: TFunction,
) => {
  return availableSeries.reduce<AgCartesianSeriesOptions[]>((acc, serie) => {
    if (usedSeries.includes(serie.id)) {
      acc.push({
        type: 'bar',
        xKey: 'date',
        yKey: serie.id,
        yName: serie.name,
        stacked: true,
        strokeWidth: 1,
        tooltip: { renderer: (params) => tooltipRenderer(params, t) },
        visible: selectedSeries[serie.id],
      });
    }
    return acc;
  }, []);
};

const reportProdAnalyzeGraphAxesOptions = () =>
  [
    {
      type: 'category',
      position: 'bottom',
      label: {
        formatter: ({ value }) => formatDateToDayAndMonth(value),
      },
    },
    {
      type: 'number',
      position: 'left',
      nice: false,
      interval: {
        step: 1,
      },
    },
  ] satisfies AgCartesianAxisOptions[];

export function ReportProdAnalyzeGraph({
  reportProdAnalyzeGraphData,
  reportProdAnalyzeUsedSeries,
  availableSeries,
}: ReportProdAnalyzeGraphProps) {
  const { ref: chartRef } = useSingleGraphReferenceContext();
  const { isMobile } = useMediaType();

  const { t, language } = useTranslation();
  const filters = useReportProdAnalyzeFilters();
  const { seriesFilter, setSeriesFilter } = useReportProdAnalyzeSeriesFilter();

  const updateVisibility = ({ itemId, visible }: AgSeriesVisibilityChange) => {
    if (itemId && typeof itemId === 'string') {
      setSeriesFilter((value) => {
        const updatedVisibility = {
          ...value,
          [filters.groupByType]: {
            ...value[filters.groupByType],
            [itemId]: visible,
          },
        };
        return updatedVisibility;
      });
    }
  };

  const initialZoom = hasInitialZoom(isMobile, filters);

  const chartOptions = {
    data: reportProdAnalyzeGraphData,
    series: buildReportProdAnalyzeSeries(
      availableSeries,
      reportProdAnalyzeUsedSeries,
      seriesFilter[filters.groupByType],
      t,
    ),
    axes: reportProdAnalyzeGraphAxesOptions(),
    legend: {
      enabled: true,
    },
    listeners: {
      seriesVisibilityChange: updateVisibility,
    },
    locale: {
      localeText: getAgChartLocale(language),
    },
    ...(isMobile
      ? {
          touch: { dragAction: 'drag' },
          zoom: {
            enabled: true,
            enableAxisDragging: false,
            enableTwoFingerZoom: true,
            buttons: {
              enabled: false,
            },
          },
          initialState: {
            zoom: initialZoom
              ? {
                  ratioX: {
                    start: 0,
                    end: 0.5,
                  },
                }
              : {},
          },
          contextMenu: {
            enabled: false,
          },
        }
      : {
          zoom: {
            anchorPointX: 'pointer',
            anchorPointY: 'pointer',
          },
        }),
  } satisfies AgChartOptions;

  useChartResize(chartRef);

  return <AgCharts options={chartOptions} ref={chartRef} />;
}
